function facetsExecuteScripts(container = false) {
  container.querySelectorAll("script").forEach((script) => {
    try {
      const newScript = document.createElement("script");
      if (script.src) {
        newScript.src = script.src;
        newScript.async = script.async;
      } else {
        newScript.textContent = script.textContent;
      }
      script.replaceWith(newScript);
    } catch (error) {
      console.warn("Failed to execute script:", script, error);
    }
  });
}

const FILTER_TOGGLE_BREAKPOINT = "(min-width: 990px)";

function updateCollectionEmptyState() {
  const gridContainer = document.getElementById("ProductGridContainer");
  if (!gridContainer) return;

  const grid = gridContainer.querySelector("#product-grid");
  if (!grid) return;

  const productCards = grid.querySelectorAll(".collection__products-grid-item");
  const existingEmptyState = grid.querySelector(".aur-coll-not-found-wrapper");

  if (productCards.length > 0) {
    if (existingEmptyState) existingEmptyState.remove();
    return;
  }

  if (existingEmptyState) return;

  const template = gridContainer.querySelector("#CollectionEmptyStateTemplate");
  if (!template || !template.content) return;

  const fragment = template.content.cloneNode(true);
  grid.appendChild(fragment);
}

function initCollectionFilterToggle(root = document) {
  if (!root) return;
  const wrappers = root.querySelectorAll("[data-filter-toggle-wrapper]");
  if (!wrappers.length) return;

  wrappers.forEach((wrapper) => {
    if (wrapper.dataset.filterToggleInitialized === "true") return;

    const button = wrapper.querySelector("[data-filter-toggle]");
    const filterHost = wrapper.closest(".aur-product-filters");
    const stickyContainer = filterHost ? filterHost.closest(".collection__filters") : null;
    if (!button || !filterHost) return;

    const mediaQuery = window.matchMedia(FILTER_TOGGLE_BREAKPOINT);
    let outsideClickHandler = null;

    const removeOutsideClickHandler = () => {
      if (!outsideClickHandler) return;
      document.removeEventListener("click", outsideClickHandler, true);
      outsideClickHandler = null;
    };

    const addOutsideClickHandler = () => {
      if (outsideClickHandler) return;
      outsideClickHandler = (event) => {
        const target = event.target;
        if (filterHost.contains(target)) return;
        removeOutsideClickHandler();
        setState(false);
      };
      document.addEventListener("click", outsideClickHandler, true);
    };

    const togglePageScrollLock = (shouldLock) => {
      const root = document.documentElement;
      if (root) root.classList.toggle("aur-filters-locked", shouldLock);
      if (document.body) document.body.classList.toggle("aur-filters-locked", shouldLock);
    };

    const setState = (isOpen) => {
      filterHost.classList.toggle("is-open", isOpen);
      button.setAttribute("aria-expanded", isOpen ? "true" : "false");
      const shouldLockScroll = isOpen && !mediaQuery.matches;
      togglePageScrollLock(shouldLockScroll);
      if (shouldLockScroll) {
        addOutsideClickHandler();
      } else {
        removeOutsideClickHandler();
      }
    };

    const syncToBreakpoint = (eventOrQuery) => {
      const matches = eventOrQuery?.matches ?? false;
      if (matches) {
        setState(true);
      } else {
        setState(false);
      }
    };

    syncToBreakpoint(mediaQuery);

    button.addEventListener("click", (event) => {
      event.preventDefault();
      if (mediaQuery.matches) return;
      const nextState = !filterHost.classList.contains("is-open");
      if (nextState) {
        button.scrollIntoView({behavior: "smooth", block: "start", inline: "nearest"});
      }
      setState(nextState);
    });

    const handleMediaChange = (event) => syncToBreakpoint(event);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleMediaChange);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handleMediaChange);
    }

    wrapper.dataset.filterToggleInitialized = "true";
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initCollectionFilterToggle());
} else {
  initCollectionFilterToggle();
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 1500);

    const facetForm = this.querySelector("form");
    
    // Listen for any input change (this covers standard inputs, custom-singleselect custom events, and checkboxes)
    facetForm?.addEventListener("input", (event) => {
      this.debouncedOnSubmit(event);
    });

    // Listen for 'change' (checkboxes in CustomMultiSelect, radios)
    facetForm?.addEventListener("change", (event) => {
      this.debouncedOnSubmit(event);
    });

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const loadingSpinners = document.querySelectorAll(".facets-container .loading__spinner, facet-filters-form .loading__spinner");
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document.getElementById("ProductGridContainer").querySelector(".collection").classList.add("loading");

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
        AuroraProduct.init();
        FacetFiltersForm.renderWishlistButton();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    if (typeof initializeScrollAnimationTrigger === "function") initializeScrollAnimationTrigger(html.innerHTML);
    AuroraProduct.init();
    FacetFiltersForm.renderWishlistButton();
  }

  static renderWishlistButton() {
    if (typeof window.SwymCallbacks !== "undefined") {
      window.SwymCallbacks.push(function (swat) {
        swat.initializeActionButtons("#ProductGridContainer");
      });
    }
    const loadingSpinners = document.querySelectorAll(".facets-container .loading__spinner, facet-filters-form .loading__spinner");
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById("ProductGridContainer");
    if (!productGridContainer) return;

    const doc = typeof html === "string" ? new DOMParser().parseFromString(html, "text/html") : html;
    const incomingGrid = doc.getElementById("ProductGridContainer");
    if (!incomingGrid) return;

    productGridContainer.innerHTML = incomingGrid.innerHTML;
    FacetFiltersForm.renderLoadMore(doc);

    // add "scroll-trigger--cancel" class to each ".scroll-trigger" element
    productGridContainer.querySelectorAll(".scroll-trigger").forEach((element) => {
      element.classList.add("scroll-trigger--cancel");
    });

    // re-execute inline scripts, specificically inline TSC-centric logic
    facetsExecuteScripts(productGridContainer);
  }

  static renderLoadMore(doc) {
    const loadMoreHost = document.querySelector("collection-load-more");
    if (!loadMoreHost || typeof loadMoreHost.refreshFromElement !== "function") return;

    const incomingLoadMore = doc.querySelector("collection-load-more");
    loadMoreHost.refreshFromElement(incomingLoadMore);
    loadMoreHost.resetOOSStateIfQueryChanged?.();
    void loadMoreHost.appendOOSProductsIfLastPage?.();
  }

  static renderFilters(html, event) {
    const newHtml = new DOMParser().parseFromString(html, "text/html");
    document.getElementById("ProductFiltersContainer").innerHTML = newHtml.getElementById("ProductFiltersContainer").innerHTML;
    if (document.getElementById("aurpaginationwrapper")) {
      document.getElementById("aurpaginationwrapper").innerHTML = newHtml.getElementById("aurpaginationwrapper")
        ? newHtml.getElementById("aurpaginationwrapper").innerHTML
        : "";
    }
    if (typeof initCollectionFilterToggle === "function") initCollectionFilterToggle();
    FacetFiltersForm.renderFacetCount(newHtml);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets-mobile", ".active-facets-desktop"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById("FacetFiltersFormMobile")?.closest("menu-drawer")?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector("show-more-button .label-show-more.hidden"));
      if (isShowingMore) {
        sourceWrapElement.querySelectorAll(".facets__item.hidden").forEach((hiddenItem) => hiddenItem.classList.replace("hidden", "show-more-item"));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static renderFacetCount(sourceDoc) {
    if (!sourceDoc) return;

    const newCountElements = sourceDoc.querySelectorAll(".active-facets__count");
    const currentCountElements = document.querySelectorAll(".active-facets__count");
    if (!newCountElements.length || !currentCountElements.length) return;

    currentCountElements.forEach((targetElement, index) => {
      const replacement = newCountElements[index] || newCountElements[0];
      targetElement.replaceWith(replacement.cloneNode(true));
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      if (value === null || value === undefined || value === "") {
        continue;
      }

      const input = form.querySelector(`[name="${key}"]`);
      if (input && input.type === "range") {
          const min = input.getAttribute("min") || "0";
          const max = input.getAttribute("max");
          
          if (key.includes("gte") && Number(value) === Number(min)) continue;
          if (key.includes("lte") && Number(value) === Number(max)) continue;
      }

      searchParams.append(key, value);
    }
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll("facet-filters-form form");
    if (event.srcElement.className == "mobile-facets__checkbox") {
      const searchParams = this.createSearchParams(event.target.closest("form"));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === "FacetSortForm" || form.id === "FacetFiltersForm" || form.id === "FacetSortDrawerForm") {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf("?") == -1 ? "" : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.fromSlider = this.querySelector("#fromSlider");
    this.toSlider = this.querySelector("#toSlider");
    this.fromInput = this.querySelector("#fromInput");
    this.toInput = this.querySelector("#toInput");

    this.fromSlider.addEventListener("input", this.onSliderInput.bind(this));
    this.toSlider.addEventListener("input", this.onSliderInput.bind(this));

    this.updateInputs();
  }

  onSliderInput(event) {
    this.updateInputs();
  }

  updateInputs() {
    this.fromInput.textContent = this.fromSlider.value;
    this.toInput.textContent = this.toSlider.value;
  }
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    if (!facetLink) return;

    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector(".aur-facet-filters-form");
    if (!form || typeof form.onActiveFilterClick !== "function") return;

    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);

class AurBar extends HTMLElement {
  constructor() {
    super();
    this.aurSmallElements = this.querySelectorAll(".aur-segment");
    this.aurShopCategoryBar = this.querySelector(".aur-shop-category-bars");
    this.hiddenInput = this.querySelector("input[type=hidden]");
    this.type = this.aurShopCategoryBar.getAttribute("data-type");
    this.aurSmallElements.forEach((element) => element.addEventListener("click", this.handleClick.bind(this)));
  }

  handleClick(event) {
    const elementTarget = event.target;
    const value = elementTarget.getAttribute("data-value");
    let nextElement = elementTarget.nextElementSibling;

    if (nextElement?.classList?.contains(this.type + "-level") && elementTarget.classList.contains(this.type + "-level")) {
      this.aurSmallElements.forEach((el) => {
        el.classList.remove(this.type);
        let val = el.getAttribute("data-value");
        if (parseInt(val) > parseInt(value)) {
          el.classList.remove(this.type + "-level");
        }
      });
      this.hiddenInput.value = value;
    } else if (elementTarget.classList.contains(this.type + "-level")) {
      this.aurSmallElements.forEach((el) => {
        el.classList.remove(this.type);
      });
      this.hiddenInput.value = null;
    } else {
      this.aurSmallElements.forEach((el) => {
        el.classList.remove(this.type);
        let val = el.getAttribute("data-value");
        if (parseInt(val) <= parseInt(value)) {
          el.classList.add(this.type + "-level");
        }
      });
      this.hiddenInput.value = value;
    }

    const facetsForm = new FacetFiltersForm();
    facetsForm.onSubmitHandler(event);
  }
}

customElements.define("aur-bar", AurBar);

class AurPagination extends HTMLElement {
  constructor() {
    super();
    this.elements = null;
    this.getPaginations();
  }

  getPaginations() {
    this.elements = document.querySelectorAll(".pagination__link:not(.account)");
    this.elements.forEach((element) => element.addEventListener("click", this.handleClick.bind(this)));
  }

  renderWishlistButton() {
    if (typeof window.SwymCallbacks !== "undefined") {
      window.SwymCallbacks.push(function (swat) {
        swat.initializeActionButtons("#ProductGridContainer");
      });
    }
  }

  handleClick(event) {
    event.preventDefault();
    const elTarget = event.target;
    const page = elTarget.getAttribute("data-page") || elTarget.closest(".pagination__link").getAttribute("data-page");
    const url = new URL(window.location.href);
    url.searchParams.set("page", page);
    const newUrl = url.toString();
    const isSearch = newUrl?.includes("/search?");
    fetch(newUrl)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        if (isSearch) {
          const searchGrid = html.querySelector(".product-grid");
          document.querySelector(".product-grid").innerHTML = searchGrid.innerHTML;
        } else {
          const prodFilter = html.getElementById("ProductFiltersContainer");
          const prodGrid = html.getElementById("ProductGridContainer");
          document.getElementById("ProductFiltersContainer").innerHTML = prodFilter.innerHTML;
          document.getElementById("ProductGridContainer").innerHTML = prodGrid.innerHTML;
        }
        FacetFiltersForm.renderLoadMore(html);
        const prodPagination = html.getElementById("aurpaginationwrapper");
        // re-execute inline scripts, specificically inline TSC-centric logic
        facetsExecuteScripts(document.getElementById("ProductGridContainer"));
        if (prodPagination) document.getElementById("aurpaginationwrapper").innerHTML = prodPagination.innerHTML;
        history.pushState({}, "", newUrl);
        this.getPaginations();
        AuroraProduct.init();
        this.renderWishlistButton();
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      });
  }
}

customElements.define("aur-pagination", AurPagination);

class CustomMultiSelect extends HTMLElement {
  constructor() {
    super();

    // Shadow DOM to hide internal inputs from iOS
    this.shadow = this.attachShadow({ mode: "open" });

    // Move UI inside the shadow DOM
    this.shadow.innerHTML = `
      <style>
        :host {
          display: block;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        /* Prevent iOS interpret this as form element */
        :host([role]) {
          role: presentation !important;
          pointer-events: none; /* Let clicks pass through if needed, though usually handled by children */
        }
        
        /* Restore pointer events for children */
        :host([role]) * {
            pointer-events: auto;
        }

        /* iOS-safe checkbox hiding (no display:none!) */
        .hidden-native-select {
          position: absolute;
          width: 0;
          height: 0;
          opacity: 0;
          pointer-events: none;
          visibility: hidden; /* <-- iOS-safe */
        }
      </style>

      <slot></slot>
    `;

    // References to original light DOM
    this.element = this;
    this.trigger = this.querySelector(".multiselect-trigger");
    this.arrow = this.querySelector(".multiselect-arrow");
    this.dropdown = this.querySelector(".multiselect-dropdown");
    this.isOpen = false;
    this.wrapper = this.closest(".facet-multiselect") || this;
    this.nativeSelects = Array.from(this.wrapper.querySelectorAll('select[data-is_multiselect="true"]'));
    this.setMobileDeviceClass();
    this.init();
  }

  init() {
    this.trigger.addEventListener("click", this.onMultiSelectTriggerClick.bind(this));

    // Close on outside click
    document.addEventListener("click", (e) => {
      if (!this.element.contains(e.target)) {
        this.close();
      }
    });

    this.initNativeMultiselectPlaceholders();
  }

  onMultiSelectTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    // Close all other open multiselects
    document.querySelectorAll("custom-multiselect").forEach((el) => {
      if (el !== this && el.isOpen) {
        el.close();
      }
    });

    // Close all single selects (custom-singleselect)
    document.querySelectorAll("custom-singleselect").forEach((el) => {
        el.close();
    });

    this.toggle();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() { 
    this.isOpen = true;
    this.dropdown.classList.add("show");
    this.trigger.classList.add("active");
  }

  close() {
    this.isOpen = false;
    this.dropdown.classList.remove("show");
    this.trigger.classList.remove("active");
  }

  setNativeMultiselectState(select) {
    const wrapper = select.closest(".facet-multiselect") || this.wrapper || this.element;
    if (!wrapper) return;

    const hasValue = Array.from(select.options || []).some((option) => option.selected && option.value !== "");
    wrapper.classList.toggle("has-value", hasValue);
  }

  initNativeMultiselectPlaceholders() {
    if (!this.nativeSelects.length) return;

    this.nativeSelects.forEach((select) => {
      this.setNativeMultiselectState(select);
      if (select.dataset.nativePlaceholderBound === "true") return;
      select.addEventListener("change", () => this.setNativeMultiselectState(select));
      select.dataset.nativePlaceholderBound = "true";
    });
  }

  isMobileDevice() {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator?.userAgent || "");
  }

  detectSimulatedMobile() {
    const userAgent = navigator.userAgent;
    const isMobileUA = /Android|iPhone|iPad|iPod/i.test(userAgent);

    const platform = navigator.platform;
    return isMobileUA && /Win|Mac|Linux/i.test(platform);
  }

  setMobileDeviceClass() {
    if (this.detectSimulatedMobile()) {
      return;
    }

    if (this.isMobileDevice()) {
      document.documentElement.classList.add("mobile-device");
    }
  }
}

customElements.define("custom-multiselect", CustomMultiSelect);

class CustomSingleSelect extends HTMLElement {
  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
    this.root.innerHTML = `
      <style>
        :host { display: inline-block; position: relative; }
        .trigger { cursor: pointer; border-radius: 8px; border: 1px solid var(--grey-500); margin: 0.5rem 0; padding: 0.5rem;}
        .singleselect-trigger { cursor: pointer; border-radius: 8px; border: 1px solid var(--grey-500); margin: 0.5rem 0; padding: 0.5rem;}
        .singleselect-trigger.active { border-radius: 8px 8px 0 0; margin-bottom: -1px;}
        .dropdown { position: relative; top: 100%; background: var(--page-bg); border: 1px solid var(--grey-500); border-top: 0; border-radius: 0 0 8px 8px; padding: 4px 0; display: none; z-index: 9999; }
        .dropdown.open { display: block; }
        .option { padding: 4px 12px; cursor: pointer; }
        .option:first-child { display:none }
        .option:hover { background: #f3f4f6; }
      </style>
      <div class="singleselect-trigger">Select...</div>
      <div class="dropdown"></div>
    `;

    this.trigger = this.root.querySelector(".singleselect-trigger");
    this.dropdown = this.root.querySelector(".dropdown");
    
    // Create and append the hidden input element
    this.hiddenInput = document.createElement('input');
    this.hiddenInput.type = 'hidden';
    
    // Get the 'name' attribute from the <custom-singleselect> element
    this.hiddenInput.name = this.getAttribute('name');
      
    // Append the hidden input to the component (Light DOM, for form submission)
    this.appendChild(this.hiddenInput); 
  }

  connectedCallback() {
    this.buildOptions();
    this.bindEvents();
  }

  buildOptions() {
    const options = this.querySelectorAll("option");
    this.dropdown.innerHTML = "";
    let initialValue = null;
    let initialText = 'Select...';

    options.forEach((opt, index) => {
      const div = document.createElement("div");
      div.className = "option";
      div.textContent = opt.textContent;
      div.dataset.value = opt.value;
      this.dropdown.appendChild(div);

      // Default to the text of the first option if it's the first loop iteration
      if (index === 0) {
        initialText = opt.textContent;
      }

      // If an option is explicitly selected, overwrite the initial text and value
      if (opt.selected) {
        initialValue = opt.value;
        initialText = opt.textContent;
      }
    });

    this.trigger.textContent = initialText;

    // Set the initial value of the hidden input
    if (initialValue !== null) {
      this.hiddenInput.value = initialValue;
    }
  }

  // Public method to close this single select
  close() {
    this.dropdown.classList.remove("open");
    this.classList.remove("opened");
    this.trigger.classList.remove("active");

  }

  bindEvents() {
    // Toggle dropdown on trigger click
    this.trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      // Close all custom-multiselects
      document.querySelectorAll("custom-multiselect").forEach((el) => {
          if (el.isOpen) el.close();
      });
      // Close all OTHER custom-singleselects
      document.querySelectorAll("custom-singleselect").forEach((el) => {
          if (el !== this) el.close();
      });
      // Toggle self
      this.trigger.classList.toggle("active");
      this.dropdown.classList.toggle("open");
      this.classList.toggle("opened");
    });

    // Select option
    this.dropdown.addEventListener("click", (e) => {
      const opt = e.target.closest(".option");
      if (!opt) return;
      // Update visible UI
      this.trigger.textContent = opt.textContent;
      // Update the hidden input value
      this.hiddenInput.value = opt.dataset.value;
      // Close dropdown
      this.close();
      // Trigger the parent FacetFiltersForm submission handler
      const form = this.closest("form");
      if (form) {
        // Dispatch 'input' event to trigger form submission/AJAX refresh
        form.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });

    // Click outside -> close
    document.addEventListener("click", () => {
      this.close();
    });
  }
}

customElements.define("custom-singleselect", CustomSingleSelect);